import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AwsRum, AwsRumConfig } from "aws-rum-web";
import jwtDecode, { JwtPayload } from "jwt-decode";
import { RumMidwayCredentialsProvider } from "@amzn/rum-midway-credentials-provider";
import { useLocation } from "react-router-dom";
import { getAppSetting } from "../../config/AppSettings";
import { getMidwayJwtToken } from "../../auth/MidwayJwtToken";

export interface RumConfig {
  appId: string;
  midwayRumRoleArn: string;
}

interface IAnalyticsContext {
  recordError: (error: Error) => void;
}

export const AnalyticsContext = createContext<IAnalyticsContext>({
  recordError: () => {},
});

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const rumConfig = getAppSetting("rumConfig") as RumConfig;
  const { pathname } = useLocation();
  const isAsanaOAuthCallback =
    pathname === "/dashboard/uat/chat/asana/callback";
  const [sageRumClient, setSageRumClient] = useState<AwsRum | null>(null);

  useEffect(() => {
    let disposed = false;
    let client: AwsRum | null = null;

    const initializeRum = async () => {
      try {
        if (isAsanaOAuthCallback) {
          return;
        }
        if (!rumConfig) {
          console.log("Skipping initializing Rum as no config available.");
          return;
        }
        console.log("Initializing Rum");

        const applicationRegion = "us-east-1";

        const token = await getMidwayJwtToken();
        const decoded: JwtPayload = jwtDecode(token);
        const currentUserAlias = decoded.sub;
        if (!currentUserAlias) {
          throw new Error("Midway token subject is unavailable");
        }

        const awsRumConfig: AwsRumConfig = {
          sessionSampleRate: 1,
          enableRumClient: true,
          sessionEventLimit: 5000,
          disableAutoPageView: true,
          endpoint: `https://dataplane.rum.${applicationRegion}.amazonaws.com`,
          telemetries: [
            "performance",
            "errors",
            [
              "interaction",
              {
                enableMutationObserver: true,
                events: [
                  { event: "pagehide", element: document },
                  { event: "pageshow", element: document },
                  { event: "load", element: document },
                  { event: "unload", element: document },
                ],
              },
            ],
            [
              "http",
              {
                recordAllRequests: true,
                addXRayTraceIdHeader: [
                  // `addXRayTraceIdHeader: true` mutates the global fetch() to add a x-amzn-trace-id header to
                  // all requests but causes cors errors from midway:
                  // >  header ‘x-amzn-trace-id’ is not allowed according to header ‘Access-Control-Allow-Headers’ from CORS
                  // Instead, only add the header to the LuscaSearch urls. See: https://github.com/aws-observability/aws-rum-web/issues/663
                  new RegExp(
                    `^https://${window.location.hostname.replaceAll(
                      /[.]/g,
                      String.raw`\$&`
                    )}`
                  ),
                ],
                urlsToExclude: [
                  /\.js$/,
                  /\.png$/,
                  /\.ico$/,
                  /\.css$/,
                  /\.svg$/,
                  /\.css$/,
                ],
              },
            ],
          ],
          allowCookies: true,
          enableXRay: true,
        };

        const midwayCredentialsProvider = new RumMidwayCredentialsProvider(
          applicationRegion,
          window.location.origin,
          rumConfig.midwayRumRoleArn
        ).ChainAnonymousCredentialsProvider;

        // Verify the current origin/role before installing RUM's global plugins.
        // Otherwise a rejected STS request leaves timers that retry indefinitely.
        await midwayCredentialsProvider();
        if (disposed) {
          return;
        }

        client = new AwsRum(
          rumConfig.appId,
          "0.0.0",
          applicationRegion,
          awsRumConfig
        );

        client.addSessionAttributes({
          username: currentUserAlias,
        });

        client.setAwsCredentials(midwayCredentialsProvider);

        setSageRumClient(client);
      } catch (error) {
        // Ignore errors thrown during CloudWatch RUM web client initialization
        console.log("Failed to initializeRum", error);
      }
    };

    initializeRum().catch((error) =>
      console.log("Failed initializeRum promise", error)
    );
    return () => {
      disposed = true;
      client?.disable();
    };
  }, [isAsanaOAuthCallback, rumConfig]);

  const recordError = useCallback(
    (error: Error) => {
      sageRumClient?.recordError(error);
    },
    [sageRumClient]
  );
  const analytics = useMemo(() => ({ recordError }), [recordError]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
}
